import PropTypes from 'prop-types';
import { Col, Row } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import DataRowContext from './DataRowContext';
import StaticData from './Controllers/StaticData';
import RowCheckbox from './RowCheckbox';
import { getRowConfig } from './dataRowUtils';
import RowControllerInput from './Controllers/RowControllerInput';
import RowControllerPhotos from './Controllers/RowControllerPhotos';
import RowControllerSelect from './Controllers/RowControllerSelect';
import DataRowControllerTags from './Controllers/DataRowControllerTags';
import condStrings from '../../utils/condStrings';

import './styles/index.scss';

export default function DataRow(props) {
  const {
    type,
    label,
    prev,
    next,
    setValue,
    readOnly,
    options,
    children,
    prevExtra,
    nextExtra,
    setValueHandler,
    onItemSelect,
    selectedItems,
    automatic,
    configOverride,
  } = props;
  const [checkedSide, setCheckedSide] = useState('prev');
  const [isConflict, setIsConflict] = useState(false);
  const [noValues, setNoValues] = useState(false);
  const [initiated, setInitiated] = useState(false);

  useEffect(() => {
    if (!initiated) {
      const config = configOverride ?? getRowConfig(type, prev, next);

      setCheckedSide(config.checked);
      setIsConflict(config.conflicts);

      if (config.checked === null) {
        setNoValues(true);
      }
    }

    setInitiated(true);
  }, [configOverride, initiated, next, prev, type]);

  useEffect(() => {
    if (checkedSide) {
      const checkedSideValue = checkedSide === 'prev' ? prev : next;

      if (setValueHandler) {
        setValue(setValueHandler(checkedSideValue));
      } else {
        setValue(checkedSideValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedSide]);

  const onChangeCheckedSide = useCallback(
    (side) => () => {
      setCheckedSide(side);
    },
    [],
  );

  return (
    <DataRowContext.Provider
      value={{
        isConflict,
        setCheckedSide,
        label,
        prev,
        next,
        type,
        readOnly,
        options,
        checkedSide,
        onItemSelect,
        selectedItems,
      }}
    >
      <div className={condStrings('data-row-item', type)}>
        <Row align="top" className="mb-2">
          <Col span="7">
            <StaticData
              side="prev"
              content={prev}
              extra={prevExtra}
              noValues={(noValues || !isConflict) && !automatic}
            />
          </Col>
          <Col span="2">
            <RowCheckbox
              side="prev"
              checked={checkedSide === 'prev'}
              onChange={onChangeCheckedSide('prev')}
              noValues={(noValues || !isConflict) && !automatic}
            />
          </Col>
          <Col span="6">{children}</Col>
          <Col span="2">
            <RowCheckbox
              side="next"
              checked={checkedSide === 'next'}
              onChange={onChangeCheckedSide('next')}
              noValues={(noValues || !isConflict) && !automatic}
            />
          </Col>
          <Col span="7">
            <StaticData
              side="next"
              content={next}
              extra={nextExtra}
              noValues={(noValues || !isConflict) && !automatic}
            />
          </Col>
        </Row>
      </div>
    </DataRowContext.Provider>
  );
}

DataRow.propTypes = {
  type: PropTypes.oneOf([
    'text',
    'select',
    'tags',
    'textarea',
    'tags_input',
    'url',
    'contacts',
    'photos',
    'bool',
    'survey',
  ]),
  children: PropTypes.element.isRequired,
  label: PropTypes.string,
  prev: PropTypes.any,
  next: PropTypes.any,
  setValue: PropTypes.func,
  readOnly: PropTypes.bool,
  options: PropTypes.array,
  prevExtra: PropTypes.any,
  nextExtra: PropTypes.any,
  setValueHandler: PropTypes.func,
  onItemSelect: PropTypes.func,
  selectedItems: PropTypes.any,
  automatic: PropTypes.bool,
  configOverride: PropTypes.shape({
    checked: PropTypes.oneOf(['prev', 'next', null]),
    conflicts: PropTypes.bool,
  }),
};

DataRow.defaultProps = {
  type: 'text',
  label: '',
  setValue: () => null,
  readOnly: false,
  prev: null,
  next: null,
  options: [],
  prevExtra: null,
  nextExtra: null,
  setValueHandler: null,
  onItemSelect: () => null,
  selectedItems: [],
  automatic: false,
  configOverride: null,
};

DataRow.Input = RowControllerInput;
DataRow.Select = RowControllerSelect;
DataRow.Tags = DataRowControllerTags;
DataRow.Photos = RowControllerPhotos;
